import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Avatar, Tabs, Tab } from '@material-ui/core'
import {
  Card,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Business, Close, Edit, Save } from '@mui/icons-material'
import { WorkspaceSections } from 'lib/enums/shared.enums'
import { getUpdatedURL } from 'lib/helpers/shared.helpers'
import {
  selectSelectedWorkspace,
  selectWorkspaceActiveTab,
} from 'lib/helpers/selectors'
import useLoader from 'hooks/Shared/useLoader'
import useEmisoresList from 'hooks/queries/emisores'
import { useUpdateWorkspace, useWorkspace } from 'hooks/queries/workspaces'
import { useAppSelector } from 'store/hooks'
import { setExternalLinkURL } from 'store/SharedData/sharedData.reducer'
import { setWorkspaceActiveTab, setSelectedWorkspace } from 'actions/workspaces'
import PaymentsContainer from 'containers/Account/Payments/PaymentsContainer/PaymentsContainer'
import RazonesSocialesContainer from 'containers/RazonesSociales/RazonesSocialesContainer/RazonesSocialesContainer'
import CollaboratorsContainer from 'containers/Workspaces/Sections/CollaboratorsContainer/CollaboratorsContainer'
import Form from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import CommentsList from '../Sections/CommentsContainer/CommentsContainer'
import Profiling from '../Sections/ProfilingContainer/ProfilingContainer'
import UserNotificationsList from '../Sections/UserNotificationsContainer/UserNotificationsContainer'
import WorkspaceInformation from '../Sections/WorkspaceInformationContainer/WorkspaceInformationContainer'
import './styles.scss'

interface IEditWorkspaceFormContainerProps {
  id: number
  onRedirect: (action: string) => void
  onFetching: (active: boolean) => void
}

const WorkspaceDetailsContainer = ({
  id,
  onRedirect,
  onFetching,
}: IEditWorkspaceFormContainerProps) => {
  const dispatch = useDispatch()

  const { pathname } = useLocation()
  const [params] = useSearchParams()

  const tabParam = params.get('tab')

  const loader = useLoader()

  const selectedWs = useAppSelector(selectSelectedWorkspace)
  const activeTab = useAppSelector(selectWorkspaceActiveTab)

  const [editName, setEditName] = useState(false)
  const [validForm, setValidForm] = useState(false)

  const wsQuery = useWorkspace(id, {
    onError: () => onRedirect('error'),
  })
  const emisoresQuery = useEmisoresList(id, { noSave: true })

  const changeWsNameMutation = useUpdateWorkspace({
    onSuccess: () => {
      wsQuery.refetch()
      setEditName(false)
      setValidForm(false)
    },
  })

  useEffect(() => {
    wsQuery.isLoading
      ? loader.primary('Cargando información del espacio de trabajo')
      : loader.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsQuery.isLoading])

  useEffect(() => {
    onFetching(wsQuery.isFetching)
  }, [wsQuery.isFetching, onFetching])

  useEffect(
    () => () => {
      dispatch(setSelectedWorkspace())
      loader.close()
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (tabParam) {
      dispatch(setWorkspaceActiveTab(tabParam as WorkspaceSections))
      const cleanUrl = getUpdatedURL(params, 'tab', pathname)
      window.history.replaceState(null, '', cleanUrl)
    }
    return () => {
      dispatch(setExternalLinkURL(undefined))
    }
  }, [dispatch, params, pathname, tabParam])

  return (
    <>
      <Card elevation={1} className="workspace-details-container">
        {/** Datos de un Workspace */}
        <div className="main-container">
          <div className="logo-container">
            <Avatar variant="circular" className="logo">
              <Business fontSize="inherit" />
            </Avatar>
          </div>
          <div className="ws-name">
            {!editName && !!selectedWs && (
              <>
                <h4>{selectedWs.name}</h4>
                <IconButton
                  className="edit-button"
                  size="small"
                  onClick={() => setEditName(true)}>
                  <Edit fontSize="inherit" />
                </IconButton>
              </>
            )}
            {editName && !!selectedWs && (
              <Form
                onSubmit={async ({ workspaceName }) =>
                  changeWsNameMutation.mutate({
                    id,
                    name: workspaceName,
                    enabled: selectedWs.enabled,
                  })
                }
                onValidity={setValidForm}>
                <TextField
                  name="workspaceName"
                  value={selectedWs?.name}
                  className="name-input"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {!changeWsNameMutation.isLoading ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditName(false)
                                setValidForm(false)
                              }}>
                              <Close fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              type="submit"
                              disabled={!validForm}>
                              <Save fontSize="inherit" />
                            </IconButton>
                          </>
                        ) : (
                          <CircularProgress color="primary" size={20} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Form>
            )}
          </div>
          {!!emisoresQuery?.data && emisoresQuery.data.length > 0 && (
            <div className="ws-emisor-info">
              <p className="person-type">{emisoresQuery.data[0].type.label}</p>
              {/* <Divider orientation="vertical" flexItem />
                        <p className="email" title={emisoresQuery.data[0].}>{props.email}</p> */}
            </div>
          )}
        </div>
        <Tabs
          className="tabs-group"
          textColor="secondary"
          value={activeTab}
          onChange={(e: React.ChangeEvent<{}>, value: WorkspaceSections) =>
            dispatch(setWorkspaceActiveTab(value))
          }>
          <Tab
            className="tab"
            label="Información"
            disableRipple
            value={WorkspaceSections.INFORMATION}
          />
          <Tab
            className="tab"
            label="Mis Pagos"
            disableRipple
            value={WorkspaceSections.PAYMENTS}
          />
          <Tab
            className="tab"
            label="Perfilamiento"
            disableRipple
            value={WorkspaceSections.FORMS}
          />
          <Tab
            className="tab"
            label="Emisor"
            disableRipple
            value={WorkspaceSections.EMISORES}
          />
          <Tab
            className="tab"
            label="Colaboradores"
            disableRipple
            value={WorkspaceSections.COLLABORATORS}
          />
          <Tab
            className="tab"
            label="Comentarios"
            disableRipple
            value={WorkspaceSections.COMMENTS}
          />
          <Tab
            className="tab"
            label="Notificaciones"
            disableRipple
            value={WorkspaceSections.NOTIFICATIONS}
          />
        </Tabs>
        <Divider />
        {activeTab === WorkspaceSections.INFORMATION &&
          !!selectedWs &&
          !!emisoresQuery.data && (
            <WorkspaceInformation
              className="content workspace-information"
              workspace={selectedWs}
              emisor={emisoresQuery.data[0]}
              onRedirect={onRedirect}
            />
          )}
        {activeTab === WorkspaceSections.PAYMENTS &&
          !wsQuery.isLoading &&
          !wsQuery.isError && <PaymentsContainer />}
        {activeTab === WorkspaceSections.FORMS &&
          !wsQuery.isLoading &&
          !wsQuery.isError && (
            <Profiling
              className="content profiling"
              workspaceId={id}
              onRedirect={onRedirect}
            />
          )}
        {activeTab === WorkspaceSections.EMISORES &&
          !!selectedWs &&
          !wsQuery.isLoading &&
          !wsQuery.isError && (
            <RazonesSocialesContainer
              className="content emisores-list"
              variant="outlined"
              workspaceId={id}
              role={selectedWs.currentRole}
              onRedirect={onRedirect}
              withHeader
            />
          )}
        {activeTab === WorkspaceSections.COLLABORATORS &&
          !wsQuery.isLoading &&
          !wsQuery.isError &&
          !!selectedWs && (
            <CollaboratorsContainer
              className="content collaborators-list"
              variant="outlined"
              workspace={selectedWs}
              onRedirect={onRedirect}
            />
          )}
        {activeTab === WorkspaceSections.COMMENTS &&
          !wsQuery.isLoading &&
          !wsQuery.isError && (
            <CommentsList
              className="content comments-list"
              workspaceId={id}
              onRedirect={onRedirect}
            />
          )}
        {activeTab === WorkspaceSections.NOTIFICATIONS &&
          !wsQuery.isLoading &&
          !wsQuery.isError && (
            <UserNotificationsList
              className="content users-notifications-list"
              workspaceId={id}
              onRedirect={onRedirect}
            />
          )}
      </Card>
    </>
  )
}

export default WorkspaceDetailsContainer
